import React, { useEffect, useState } from 'react';
import { usePayment } from '../contexts/PaymentContext';
import { useAuth } from '../contexts/AuthContext';

const PaymentCallback = () => {
  const { processStripeReturn, paymentStatus, paymentMessage } = usePayment();
  const { loading: authLoading, currentUser } = useAuth();
  const [callbackProcessed, setCallbackProcessed] = useState(false);

  useEffect(() => {
    // Only process payment return when:
    // 1. Auth is not loading (auth state is determined)
    // 2. We have a current user (user is authenticated)
    // 3. We haven't already processed this callback
    if (!authLoading && currentUser && !callbackProcessed) {
      const processPaymentReturn = async () => {
        console.log('🔄 Processing payment return callback...');
        
        try {
          const handled = await processStripeReturn();
          
          if (handled) {
            console.log('✅ Payment return processed successfully');
            setCallbackProcessed(true);
          } else {
            console.log('ℹ️ No payment return to process');
          }
        } catch (error) {
          console.error('❌ Error processing payment return:', error);
        }
      };

      processPaymentReturn();
    }
  }, [authLoading, currentUser, callbackProcessed, processStripeReturn]);

  // Show loading while auth is loading or processing payment
  if (authLoading || (!callbackProcessed && !paymentStatus)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Processing Payment...
          </h2>
          <p className="text-gray-600">
            Please wait while we verify your payment with Stripe.
          </p>
        </div>
      </div>
    );
  }

  // Show payment status if we have one
  if (paymentStatus) {
    const statusConfig = {
      success: {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconColor: 'text-green-600',
        titleColor: 'text-green-900',
        icon: '✅'
      },
      error: {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconColor: 'text-red-600',
        titleColor: 'text-red-900',
        icon: '❌'
      },
      info: {
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconColor: 'text-blue-600',
        titleColor: 'text-blue-900',
        icon: 'ℹ️'
      },
      processing: {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        iconColor: 'text-yellow-600',
        titleColor: 'text-yellow-900',
        icon: '⏳'
      }
    };

    const config = statusConfig[paymentStatus] || statusConfig.info;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className={`max-w-md w-full ${config.bgColor} ${config.borderColor} border rounded-lg p-8 text-center`}>
          <div className={`text-4xl mb-4 ${config.iconColor}`}>
            {config.icon}
          </div>
          <h2 className={`text-xl font-semibold ${config.titleColor} mb-4`}>
            {paymentStatus === 'success' && 'Payment Successful!'}
            {paymentStatus === 'error' && 'Payment Error'}
            {paymentStatus === 'info' && 'Payment Information'}
            {paymentStatus === 'processing' && 'Processing Payment...'}
          </h2>
          <p className="text-gray-700 mb-6">
            {paymentMessage}
          </p>
          
          {paymentStatus === 'success' && (
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/profile'}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View My Profile
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Continue Using App
              </button>
            </div>
          )}
          
          {paymentStatus === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/billing'}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Back to App
              </button>
            </div>
          )}
          
          {(paymentStatus === 'info' || paymentStatus === 'processing') && (
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to App
            </button>
          )}
        </div>
      </div>
    );
  }

  // Fallback - redirect to home if no payment processing needed
  // useEffect(() => {
    if (callbackProcessed && !paymentStatus) {
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  // }, [callbackProcessed, paymentStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <p className="text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
};

export default PaymentCallback;